/**
 * Typed client for member accounts.
 *
 * These are ordinary site accounts — separate from the officer logins behind
 * /admin, with their own cookies. Any future club project should build on this
 * rather than inventing a second notion of "signed in".
 */

import { requestJson, type RequestOptions } from "./httpClient";

export const MEMBER_CSRF_COOKIE = "tau_member_csrf";

/**
 * Mirrors the server's password policy so the sign-up form can state it without
 * a request for three numbers. `GET /api/accounts/rules` remains the source of
 * truth and is worth calling if these ever need to differ per deployment.
 */
export const MIN_PASSWORD_LENGTH = 12;

export interface MemberSession {
  readonly username: string;
  readonly user_id: number;
  readonly expires_at: string;
  readonly csrf_token: string;
  /**
   * Labels the profile, nothing more. Officer powers are checked server-side
   * against an admin session — never against this flag.
   */
  readonly is_officer: boolean;
}

export interface RatedPuzzle {
  readonly id: number;
  readonly title: string;
  /** What you said. */
  readonly difficulty: number;
  /** What the person who added the puzzle said. */
  readonly creator_difficulty: number | null;
  /** What everyone averages to, including you. */
  readonly club_difficulty: number | null;
  readonly quality: number | null;
  readonly character: string | null;
  readonly solved: boolean | null;
}

export interface Profile {
  readonly username: string;
  readonly member_since: string;
  readonly is_officer: boolean;
  readonly rated_count: number;
  readonly average_difficulty_given: number | null;
  readonly solved_count: number;
  readonly rated: readonly RatedPuzzle[];
}

export interface AccountRules {
  readonly min_username_length: number;
  readonly max_username_length: number;
  readonly min_password_length: number;
}

export interface SavedResult {
  readonly saved: boolean;
  readonly message: string;
}

const request = <T,>(path: string, options?: RequestOptions) =>
  requestJson<T>("/api/accounts", path, options, MEMBER_CSRF_COOKIE);

export const accountsApi = {
  rules: (signal?: AbortSignal) => request<AccountRules>("/rules", { signal }),
  session: (signal?: AbortSignal) => request<MemberSession>("/session", { signal }),
  profile: (signal?: AbortSignal) => request<Profile>("/profile", { signal }),
  register: (username: string, password: string) =>
    request<MemberSession>("/register", { method: "POST", body: { username, password } }),
  login: (username: string, password: string) =>
    request<MemberSession>("/login", { method: "POST", body: { username, password } }),
  logout: () => request<SavedResult>("/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<SavedResult>("/password", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    }),
};
